import {
	Component,
	Input,
	OnInit,
	Output,
	EventEmitter,
	OnDestroy,
} from '@angular/core';
import { createId, getFileFromBase64 } from '@core/util/file';
import { getMimeType } from '@core/util/mime';
import { LLCSourceTag, LLCTransactionIdTag, Tags } from '@core/util/tags';
import { LLCDocumentDownload } from '@fnModels/llc';
import { LLCFunctions } from '@functions/llc.service';
import { MatterDocumentType } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UploadFileStatus } from '@state/utils/models';
import LogRocket from 'logrocket';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICON } from '@core/util/icon';

@UntilDestroy()
@Component({
	selector: 'app-llc-file-download-status',
	templateUrl: './llc-file-download-status.component.html',
	standalone: false,
})
export class LlcFileDownloadStatusComponent implements OnInit, OnDestroy {
	public ICON = ICON;
	public downloading = false;
	public success = false;
	public uuid;
	public fileUpload: FileUpload;

	private submitSubscription: Subscription;

	@Input() submit: Observable<void>;
	@Input() documentId: string;
	@Input() transactionId: string;

	@Input() selected = false;

	@Output() onError: EventEmitter<string> = new EventEmitter<string>();
	@Output() onSuccess: EventEmitter<boolean> = new EventEmitter<boolean>();

	@Select(FileUploadState.getFileStatus) fileStatusFilter$: Observable<
		(filter: string) => FileUpload
	>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private llcFunctions: LLCFunctions,
		private matterDispatch: MatterDispatch,
	) {}

	ngOnInit(): void {
		this.submitSubscription = this.submit.subscribe(() => this._submit());
	}

	ngOnDestroy() {
		this.submitSubscription.unsubscribe();
	}

	getFileStatus$(docId) {
		return this.fileStatusFilter$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(docId)),
		);
	}

	async _submit() {
		if (this.selected) {
			try {
				this.downloading = true;
				this.uuid = createId();
				// request document
				const downloadedDoc = (await this.llcFunctions
					.getDocument(this.transactionId, this.documentId)
					.toPromise()) as LLCDocumentDownload;
				// upload document
				const mimeType = getMimeType[downloadedDoc.fileFormat.toLowerCase()];
				if (!mimeType) {
					console.log(`Unable to find MIME type ${downloadedDoc.fileFormat}`);
					LogRocket.error(
						`Unable to find MIME type ${downloadedDoc.fileFormat}`,
					);
					throw Error(`Unable to find MIME type ${downloadedDoc.fileFormat}`);
				}
				const fileName = downloadedDoc.documentNames.find(
					(x) => x?.language?.toLowerCase() === 'english',
				)?.name;
				const file = getFileFromBase64(downloadedDoc.data, fileName, mimeType);
				const id = createId();
				const llctag = Tags.LLC;
				const mortgageTag = LLCTransactionIdTag(this.transactionId);
				const sourceTag = LLCSourceTag(downloadedDoc.originator);

				this.matterDispatch.UploadMatterFile(
					id,
					this.matterId,
					fileName,
					file,
					MatterDocumentType.MORTGAGE,
					[llctag, mortgageTag, sourceTag],
				);

				this.getFileStatus$(id).subscribe((_fileUpload) => {
					this.fileUpload = _fileUpload;

					if (_fileUpload.status === UploadFileStatus.SUCCESS) {
						this.success = true;
						this.downloading = false;
						this.onSuccess.emit(true);
					}

					if (_fileUpload.status === UploadFileStatus.FAILED) {
						this.logError(_fileUpload.error);
					}
				});
			} catch (err) {
				this.logError(err);
				this.downloading = false;
			}
		}
	}

	logError(err) {
		this.onError.emit(this.documentId);
		LogRocket.error('Unable to download / upload file from llc', err);
		console.log('Unable to download / upload file from llc', err);
	}
}
